import { Text } from '@/components/atoms/text';
import {
  CardButton,
  CardContainer,
  CardContent,
  CardHeader,
  CardInfoContainer,
  CardInfoItem,
} from './styles';
import { IconButton } from '@/components/atoms/Button';
import {
  Close,
  CorporateFareOutlined,
  Groups2Outlined,
  LocationOnOutlined,
} from '@mui/icons-material';
import { EcoLeafIcon } from '@/components/atoms/customIcons/ecoLeaf';
import { useTheme } from '@mui/material/styles';
import { GeometryData } from '@/context/geometry';
import { useTranslation, TranslationKey } from '@/hooks/useTranslation';
import { SafeMarkdown } from '@/components/atoms/markdown/safe';
import { useMemo } from 'react';
import { Skeleton } from '@mui/material';
import { Link } from 'react-router-dom';

interface MapCardProps {
  geometryData: GeometryData;
  hasDimensionsScores?: boolean;
  variant?: 'risk' | 'opportunity';
  loadingData?: boolean;
  href?: string;
  onClick?: () => void;
  onClose: () => void;
}

export const MapCard = ({
  geometryData,
  hasDimensionsScores,
  variant,
  loadingData,
  href,
  onClick,
  onClose,
}: MapCardProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const translateRegion = (regionName: string) => {
    if (!regionName) return '';
    const normalizedRegion = regionName.toLowerCase().trim();
    const translationKey = `page.municipality.municipality.region.${normalizedRegion}` as TranslationKey;
    return t(translationKey);
  };

  const scores = useMemo(() => {
    if (hasDimensionsScores && geometryData.dimensions_scores?.length > 0) {
      switch (variant) {
        case 'opportunity':
          return geometryData.dimensions_scores[0].score_opportunity;
        default:
          return geometryData.dimensions_scores[0].score_risk;
      }
    }

    switch (variant) {
      case 'opportunity':
        return geometryData.municipality_score.general_score_opportunity;
      default:
        return geometryData.municipality_score.general_score_risk;
    }
  }, [
    hasDimensionsScores,
    variant,
    geometryData.dimensions_scores,
    geometryData.municipality_score,
  ]);

  const infoItems = [
    {
      value: geometryData.biome,
      icon: <EcoLeafIcon sx={{ width: 20, height: 20, color: theme.palette.action.active }} />,
    },
    {
      value: translateRegion(geometryData.name_region),
      icon: <LocationOnOutlined sx={{ color: theme.palette.action.active }} />,
    },
    {
      value: String(geometryData.population),
      icon: <Groups2Outlined sx={{ color: theme.palette.action.active }} />,
    },
    {
      value: geometryData.classification_type,
      icon: <CorporateFareOutlined sx={{ color: theme.palette.action.active }} />,
    },
  ];

  return (
    <CardContainer>
      <CardHeader>
        {loadingData ? (
          <Skeleton
            variant="text"
            width="60%"
            height={24}
            animation="wave"
            sx={{ transform: 'none' }}
          />
        ) : (
          <Text variant="subtitle2" color="text.primary">
            <SafeMarkdown text={`${geometryData.name_municipality}`} /> ({geometryData.sigla_uf})
          </Text>
        )}

        <IconButton
          onClick={onClose}
          sx={{
            color: theme.palette.text.primary,
            padding: theme.spacing(0.5),
          }}
        >
          <Close color="inherit" />
        </IconButton>
      </CardHeader>

      <CardContent>
        <Text variant="subtitle2" color="text.secondary">
          {loadingData && hasDimensionsScores ? (
            <Skeleton
              variant="text"
              width="100%"
              height={22}
              animation="wave"
              sx={{ transform: 'none' }}
            />
          ) : hasDimensionsScores && geometryData.dimensions_scores?.length > 0 ? (
            geometryData.dimensions_scores.map((item) => (
              <SafeMarkdown key={item.dimension.id} text={item.dimension.name} />
            ))
          ) : (
            <SafeMarkdown text={t('page.municipality.municipality.card.scores.footer')} />
          )}
        </Text>

        {loadingData ? (
          <Skeleton
            variant="text"
            width="50%"
            height={32}
            animation="wave"
            sx={{ transform: 'none', margin: '0 auto' }}
          />
        ) : (
          <Text variant="h5" color="text.primary">
            {scores}
          </Text>
        )}

        {loadingData ? (
          <Skeleton
            variant="text"
            width="50%"
            height={22}
            animation="wave"
            sx={{ transform: 'none', margin: '0 auto' }}
          />
        ) : (
          <Text variant="subtitle2" color="text.secondary">
            <SafeMarkdown
              text={
                variant === 'risk'
                  ? String(
                      hasDimensionsScores && geometryData.dimensions_scores?.length > 0
                        ? geometryData.dimensions_scores[0].classification_risk
                        : geometryData.municipality_score.classification_risk
                    )
                  : String(
                      hasDimensionsScores && geometryData.dimensions_scores?.length > 0
                        ? geometryData.dimensions_scores[0].classification_opportunity
                        : geometryData.municipality_score.classification_opportunity
                    )
              }
            />
          </Text>
        )}

        <CardInfoContainer>
          {loadingData ? (
            <>
              {[1, 2, 3, 4].map((index) => (
                <CardInfoItem key={index}>
                  <Skeleton
                    variant="circular"
                    width={20}
                    height={20}
                    animation="wave"
                    sx={{ transform: 'none' }}
                  />
                  <Skeleton
                    variant="text"
                    width={80}
                    height={16}
                    animation="wave"
                    sx={{ transform: 'none' }}
                  />
                </CardInfoItem>
              ))}
            </>
          ) : (
            infoItems.map((item) => (
              <CardInfoItem key={item.value}>
                {item.icon}
                <Text variant="caption" color="text.primary">
                  <SafeMarkdown text={item.value || ''} />
                </Text>
              </CardInfoItem>
            ))
          )}
        </CardInfoContainer>

        {href ? (
          <CardButton
            component={Link}
            to={href}
            variant="outlined"
            fullWidth
            onClick={
              onClick
                ? (e: React.MouseEvent) => {
                    e.preventDefault();
                    onClick();
                  }
                : undefined
            }
          >
            <SafeMarkdown text={t('button.button.see_more')} />
          </CardButton>
        ) : (
          <CardButton onClick={onClick} variant="outlined" fullWidth>
            <SafeMarkdown text={t('button.button.see_more')} />
          </CardButton>
        )}
      </CardContent>
    </CardContainer>
  );
};
